import {
  DocumentTextIcon,
  PhotoIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { JWT_HOST_API } from 'configs/auth.config';
import { formatDisplayDate } from 'utils/dateUtils';

export const getDocumentUrl = (filePath) => {
  if (!filePath) return '';
  if (typeof filePath === 'string' && (filePath.startsWith('http://') || filePath.startsWith('https://'))) {
    return filePath;
  }

  const normalized = filePath.replace(/\\/g, '/');
  let relativePath = normalized;
  const index = normalized.toLowerCase().indexOf('uploads/');
  if (index !== -1) {
    relativePath = normalized.substring(index);
  }

  return `${JWT_HOST_API}${relativePath}`;
};

// File type icon mapping
const FILE_TYPE_ICONS = {
  pdf: DocumentTextIcon,
  doc: DocumentTextIcon,
  docx: DocumentTextIcon,
  xls: TableCellsIcon,
  xlsx: TableCellsIcon,
  csv: TableCellsIcon,
  jpg: PhotoIcon,
  jpeg: PhotoIcon,
  png: PhotoIcon,
  gif: PhotoIcon,
  svg: PhotoIcon,
  webp: PhotoIcon,
  default: DocumentTextIcon,
};

// File type color mapping
const FILE_TYPE_COLORS = {
  pdf: "text-red-500",
  doc: "text-blue-500",
  docx: "text-blue-500",
  xls: "text-green-500",
  xlsx: "text-green-500",
  csv: "text-green-500",
  jpg: "text-purple-500",
  jpeg: "text-purple-500",
  png: "text-purple-500",
  gif: "text-purple-500",
  svg: "text-purple-500",
  webp: "text-purple-500",
  default: "text-gray-500",
};

export const getFileIcon = (fileType) => {
  const Icon = FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS.default;
  const colorClass = FILE_TYPE_COLORS[fileType] || FILE_TYPE_COLORS.default;
  return { Icon, colorClass };
};

export const formatDate = (dateString) => {
  return formatDisplayDate(dateString);
};

export const getInitials = (name) => {
  if (!name || typeof name !== 'string') {
    return 'U';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 0) return 'U';

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const VERIFICATION_STATUS_LABELS = {
  0: "Pending",
  1: "Verified",
  2: "Rejected",
};

const VERIFICATION_STATUS_COLORS = {
  0: "warning",
  1: "success",
  2: "error",
};

export const getVerificationStatusInfo = (status) => {
  const numericStatus = typeof status === "number" ? status : parseInt(status ?? 0, 10) || 0;
  return {
    label: VERIFICATION_STATUS_LABELS[numericStatus] || VERIFICATION_STATUS_LABELS[0],
    color: VERIFICATION_STATUS_COLORS[numericStatus] || VERIFICATION_STATUS_COLORS[0],
    value: numericStatus,
  };
};

const STATUS_METADATA_LABELS = {
  1: {
    title: "Verified By",
    dateLabel: "Verified On",
    remarkLabel: "Verification Remark",
  },
  2: {
    title: "Rejected By",
    dateLabel: "Rejected On",
    remarkLabel: "Rejection Remark",
  },
  0: {
    title: "Assigned To",
    dateLabel: "Assigned On",
    remarkLabel: "Notes",
  },
};

export const getVerificationMetadataLabels = (status) => {
  const info = getVerificationStatusInfo(status);
  return {
    ...info,
    meta: STATUS_METADATA_LABELS[info.value] || STATUS_METADATA_LABELS[0],
  };
};
